// ---------------------------------------------------------------------------
// QueryDataSetCards — the two-column Query set + Data set section.
//
// The query set is always present (every eval needs queries); the data set
// is optional and toggled via the inline Switch in the card header. When
// enabled, the data set also supports an Existing-tab picker that mounts
// the Data Lake auth flow lazily (only once the user opens the tab).
// ---------------------------------------------------------------------------

import {
  Body1,
  MessageBar,
  MessageBarBody,
  Switch,
  mergeClasses,
} from '@fluentui/react-components';

import { useStyles } from '../../EvaluationTemplateForm.style.js';
import type {
  DataLakeFileSelector,
  DataSourceMode,
} from '../../EvaluationTemplateForm.types.js';
import type { InlineItemsAnalysis } from '../../EvaluationTemplateForm.utils.js';
import { analyzeInlineItems } from '../../EvaluationTemplateForm.utils.js';
import { ExistingDataSetPicker } from '../ExistingDataSetPicker/ExistingDataSetPicker.js';
import { ItemsSourcePicker } from '../ItemsSourcePicker/ItemsSourcePicker.js';

export interface QueryDataSetCardsProps {
  viewOnly: boolean;

  // Query set
  querySetMode: DataSourceMode;
  onQuerySetModeChange: (mode: DataSourceMode) => void;
  queryInlineText: string;
  onQueryInlineTextChange: (text: string) => void;
  queryFileName: string | null;
  queryFileItemCount: number | null;
  queryFileAnalysis?: InlineItemsAnalysis | null;
  queryFileError: string | null;
  onQueryFile: (file: File) => Promise<void>;
  queryErrors?: string[];

  // Data set
  dataSetEnabled: boolean;
  onDataSetEnabledChange: (enabled: boolean) => void;
  dataSetMode: DataSourceMode;
  onDataSetModeChange: (mode: DataSourceMode) => void;
  dataInlineText: string;
  onDataInlineTextChange: (text: string) => void;
  dataFileName: string | null;
  dataFileItemCount: number | null;
  dataFileAnalysis?: InlineItemsAnalysis | null;
  dataFileError: string | null;
  onDataFile: (file: File) => Promise<void>;
  dataSetExistingId: string | null;
  dataSetExistingVersion: number | null;
  onDataSetExistingChange: (id: string | null, version: number | null) => void;
  dataSetFileSelector: DataLakeFileSelector[];
  onDataSetFileSelectorChange: (selectors: DataLakeFileSelector[]) => void;
  dataErrors?: string[];
}

export function QueryDataSetCards({
  viewOnly,
  querySetMode,
  onQuerySetModeChange,
  queryInlineText,
  onQueryInlineTextChange,
  queryFileName,
  queryFileItemCount,
  queryFileAnalysis = null,
  queryFileError,
  onQueryFile,
  queryErrors = [],
  dataSetEnabled,
  onDataSetEnabledChange,
  dataSetMode,
  onDataSetModeChange,
  dataInlineText,
  onDataInlineTextChange,
  dataFileName,
  dataFileItemCount,
  dataFileAnalysis = null,
  dataFileError,
  onDataFile,
  dataSetExistingId,
  dataSetExistingVersion,
  onDataSetExistingChange,
  dataSetFileSelector,
  onDataSetFileSelectorChange,
  dataErrors = [],
}: QueryDataSetCardsProps) {
  const styles = useStyles();
  const queryValidationErrors = summarizeSourceErrors(
    'QuerySet',
    queryErrors,
    true,
  );
  const dataValidationErrors = summarizeSourceErrors(
    'DataSet',
    dataErrors,
    dataSetEnabled,
  );
  const queryInlineAnalysis = analyzeInlineItems(queryInlineText);
  const dataInlineAnalysis = analyzeInlineItems(dataInlineText);
  const queryItemCount = sourceItemCount(
    querySetMode,
    queryInlineAnalysis,
    queryFileItemCount,
    queryFileError,
  );
  const dataItemCount = dataSetEnabled
    ? sourceItemCount(
        dataSetMode,
        dataInlineAnalysis,
        dataFileItemCount,
        dataFileError,
      )
    : 1;

  return (
    <>
      <div className={styles.twoColumn}>
        <section className={styles.card} aria-labelledby="card-queryset">
          <div
            id="card-queryset"
            className={mergeClasses(styles.cardLabel, styles.sourceCardHeader)}
          >
            Query set
          </div>
          <div className={styles.cardBody}>
            <ItemsSourcePicker
              mode={querySetMode}
              onModeChange={onQuerySetModeChange}
              inlineText={queryInlineText}
              inlineAnalysis={queryInlineAnalysis}
              onInlineChange={onQueryInlineTextChange}
              fileName={queryFileName}
              fileItemCount={queryFileItemCount}
              fileAnalysis={queryFileAnalysis}
              fileError={queryFileError}
              onFile={onQueryFile}
              fieldId="query-set-file"
              validationErrors={queryValidationErrors}
            />
          </div>
        </section>

        <section className={styles.card} aria-labelledby="card-dataset">
          <div
            id="card-dataset"
            className={mergeClasses(styles.cardLabel, styles.sourceCardHeader)}
          >
            Data set
            <Switch
              checked={dataSetEnabled}
              onChange={(_e, data) => onDataSetEnabledChange(data.checked)}
              label="Enable"
              aria-label="Enable data set"
            />
          </div>
          <div className={styles.cardBody}>
            {!dataSetEnabled &&
              dataValidationErrors.map((error) => (
                <MessageBar key={error} intent="error" role="alert">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              ))}
            {!dataSetEnabled && dataValidationErrors.length === 0 && (
              <Body1 className={styles.helperText}>
                No data set — tasks will use a single empty data item. Enable to
                join query items against reference data (ground truth, context,
                labels).
              </Body1>
            )}
            {dataSetEnabled && (
              <ItemsSourcePicker
                mode={dataSetMode}
                onModeChange={onDataSetModeChange}
                inlineText={dataInlineText}
                inlineAnalysis={dataInlineAnalysis}
                onInlineChange={onDataInlineTextChange}
                fileName={dataFileName}
                fileItemCount={dataFileItemCount}
                fileAnalysis={dataFileAnalysis}
                fileError={dataFileError}
                onFile={onDataFile}
                fieldId="data-set-file"
                validationErrors={dataSetEnabled ? dataValidationErrors : []}
                // Picker slot — only mounts (and triggers Data Lake auth)
                // once the user actually opens the Existing tab. Disabled in
                // view mode but stays visible so users can see what was saved.
                existingPicker={
                  <ExistingDataSetPicker
                    selectedId={dataSetExistingId}
                    selectedVersion={dataSetExistingVersion}
                    disabled={viewOnly}
                    onSelect={(sel) =>
                      onDataSetExistingChange(
                        sel?.id ?? null,
                        sel?.version ?? null,
                      )
                    }
                    fileSelector={dataSetFileSelector}
                    onFileSelectorChange={onDataSetFileSelectorChange}
                  />
                }
              />
            )}
          </div>
        </section>
      </div>
      {queryItemCount !== null && dataItemCount !== null && (
        <Body1 className={styles.noteCallout} role="status">
          Estimated trials: {String(queryItemCount * dataItemCount)} (
          {formatItemCount(queryItemCount, 'query')} ×{' '}
          {dataSetEnabled
            ? formatItemCount(dataItemCount, 'data')
            : '1 implicit data item'}
          )
        </Body1>
      )}
    </>
  );
}

function sourceItemCount(
  mode: DataSourceMode,
  inlineAnalysis: InlineItemsAnalysis,
  fileItemCount: number | null,
  fileError: string | null,
): number | null {
  if (mode === 'inline') {
    // No parsed items (empty or a hard validation error) → no estimate, so the
    // UI doesn't show a misleading "0 trials" for invalid input.
    return inlineAnalysis.items === null ? null : inlineAnalysis.count;
  }
  if (mode === 'file') return fileError === null ? fileItemCount : null;
  return null;
}

function formatItemCount(count: number, label: 'query' | 'data'): string {
  return `${String(count)} ${label} ${count === 1 ? 'item' : 'items'}`;
}

function summarizeSourceErrors(
  source: 'QuerySet' | 'DataSet',
  errors: string[],
  enabled: boolean,
): string[] {
  if (errors.length === 0) return [];
  const pathPattern = new RegExp(`${source}\\.([A-Za-z0-9_.]+)`, 'g');
  const paths = new Set<string>();
  for (const error of errors) {
    for (const match of error.matchAll(pathPattern)) {
      if (match[1] !== undefined) paths.add(match[1]);
    }
  }
  if (paths.size === 0) return errors;
  const fields = [...paths].join(', ');
  if (source === 'DataSet' && !enabled) {
    return [`DataSet is required. Enable it and provide: ${fields}.`];
  }
  if (source === 'QuerySet') {
    return [`QuerySet items must contain: ${fields}.`];
  }
  return [`DataSet items must contain: ${fields}.`];
}
